import React, { useState } from 'react';
import { sendSlackMessageTracked } from "../../models/notifications";

const RED   = "#C0392B";
const DARK  = "#111111";
const MID   = "#6b7280";
const RULE  = "#e5e7eb";

const s = {
    card: {
        background: "#fff",
        width: "100%",
        maxWidth: 480,
        margin: "0 auto",
        border: `1px solid ${RULE}`,
    },
    header: {
        padding: "32px 36px 24px",
        borderBottom: `1px solid ${RULE}`,
    },
    stepTrack: {
        display: "flex",
        alignItems: "center",
        marginBottom: 24,
    },
    stepItem: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        flex: 1,
    },
    stepConnector: {
        flex: 1,
        height: 1,
        background: RULE,
        margin: "0 8px",
    },
    stepTitle: {
        fontSize: "1.15rem",
        fontWeight: 700,
        letterSpacing: "-0.02em",
        marginBottom: 4,
        color: DARK,
    },
    stepSubtitle: {
        fontSize: "0.75rem",
        color: MID,
    },
    body: {
        padding: "28px 36px",
    },
    fieldLabel: {
        display: "block",
        fontSize: "0.8rem",
        fontWeight: 600,
        color: DARK,
        marginBottom: 14,
    },
    input: {
        display: "block",
        width: "100%",
        padding: "11px 14px",
        fontFamily: "inherit",
        fontSize: "0.875rem",
        color: DARK,
        background: "#fff",
        border: `1px solid ${RULE}`,
        outline: "none",
        borderRadius: 0,
        marginBottom: 10,
        WebkitAppearance: "none",
    },
    textarea: {
        display: "block",
        width: "100%",
        padding: "11px 14px",
        fontFamily: "inherit",
        fontSize: "0.875rem",
        color: DARK,
        background: "#fff",
        border: `1px solid ${RULE}`,
        outline: "none",
        borderRadius: 0,
        resize: "none",
        WebkitAppearance: "none",
    },
    footer: {
        padding: "16px 36px 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },
    btnBack: {
        background: "none",
        border: "none",
        fontFamily: "inherit",
        fontSize: "0.8rem",
        color: "#9ca3af",
        cursor: "pointer",
        padding: 0,
    },
    btnPrimary: (enabled) => ({
        fontFamily: "inherit",
        fontSize: "0.8rem",
        fontWeight: 600,
        color: "#fff",
        background: enabled ? RED : "#d1d5db",
        border: "none",
        padding: "10px 24px",
        cursor: enabled ? "pointer" : "not-allowed",
        letterSpacing: "0.01em",
    }),
    note: {
        padding: "0 36px 24px",
        fontSize: "0.72rem",
        color: "#9ca3af",
        lineHeight: 1.6,
    },
    radioItem: (selected) => ({
        display: "flex",
        alignItems: "center",
        gap: 10,
        cursor: "pointer",
        padding: "10px 12px",
        border: `1px solid ${selected ? RED : RULE}`,
        background: selected ? "#fef8f8" : "#fff",
        marginBottom: 8,
    }),
    radioBox: (selected) => ({
        width: 16,
        height: 16,
        border: `1px solid ${selected ? RED : RULE}`,
        background: selected ? RED : "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    }),
    radioDot: {
        width: 6,
        height: 6,
        background: "#fff",
        borderRadius: "50%",
    },
    radioLabel: {
        fontSize: "0.875rem",
        color: DARK,
        fontWeight: 500,
    },
    successWrap: {
        padding: "56px 36px",
        textAlign: "center",
    },
    successRule: {
        width: 40,
        height: 1,
        background: RED,
        margin: "0 auto 28px",
    },
    successTitle: {
        fontSize: "1.3rem",
        fontWeight: 700,
        letterSpacing: "-0.02em",
        marginBottom: 10,
        color: DARK,
    },
    successText: {
        fontSize: "0.85rem",
        color: MID,
        lineHeight: 1.7,
    },
};

const STEPS = [
    { label: "Your Info",  title: "Tell us about yourself",  subtitle: "Step 1 of 3" },
    { label: "Your Role",  title: "What describes you?",      subtitle: "Step 2 of 3" },
    { label: "A Few More", title: "One last thing",           subtitle: "Step 3 of 3" },
];

const ROLES = ["Parent", "Student", "Counselor", "Educator", "Other"];

function FocusInput({ style: extraStyle, ...props }) {
    const [focused, setFocused] = useState(false);
    return (
        <input
            {...props}
            style={{ ...s.input, ...extraStyle, borderColor: focused ? RED : RULE }}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
        />
    );
}

function FocusTextarea({ ...props }) {
    const [focused, setFocused] = useState(false);
    return (
        <textarea
            {...props}
            style={{ ...s.textarea, borderColor: focused ? RED : RULE }}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
        />
    );
}

export default function SecondaryForm() {
    const [step, setStep]           = useState(0); // 0-indexed
    const [submitted, setSubmitted] = useState(false);
    const [formData, setFormData] = useState({
        firstName: '', lastName: '', email: '',
        role: '', measureSuccess: '', hearAbout: '',
    });

    const set = (field) => (e) => setFormData(prev => ({ ...prev, [field]: e.target.value }));
    const setRole = (role) => setFormData(prev => ({ ...prev, role }));

    const canAdvance = () => {
        if (step === 0) return formData.firstName && formData.lastName && formData.email;
        if (step === 1) return !!formData.role;
        if (step === 2) return formData.measureSuccess && formData.hearAbout;
        return false;
    };

    const handleSubmit = () => {
        sendSlackMessageTracked(
            `Extern Academy Lead submitted the form:\n${JSON.stringify(formData)}`,
            "form-submissions"
        );
        setSubmitted(true);
    };

    if (submitted) {
        return (
            <>
            <style>{`
                .sf-card { transition: box-shadow 0.2s ease; }
                .sf-card:hover { box-shadow: 4px 8px 8px hsl(0deg 0% 0% / 0.18); }
            `}</style>
            <div className="sf-card" style={s.card}>
                <div style={s.successWrap}>
                    <div style={s.successRule} />
                    <p style={s.successTitle}>You're all set.</p>
                    {/*<p style={s.successText}>*/}
                    {/*    All set. We'll send a complete guide to<br />*/}
                    {/*    Extern Academy's programs to <strong>{formData.email}</strong> shortly.*/}
                    {/*</p>*/}
                </div>
            </div>
            </>
        );
    }

    return (
        <>
        <style>{`
            .sf-card { transition: box-shadow 0.2s ease; }
            .sf-card:hover { box-shadow: 4px 8px 8px hsl(0deg 0% 0% / 0.18); }
        `}</style>
        <div className="sf-card" style={s.card}>

            {/* Header */}
            <div style={s.header}>
                <div style={s.stepTrack}>
                    {STEPS.map((st, i) => (
                        <React.Fragment key={i}>
                            <div style={s.stepItem}>
                                <div style={{
                                    width: 24, height: 24,
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    fontSize: "0.65rem", fontWeight: 700, flexShrink: 0,
                                    border: `1px solid ${i <= step ? RED : RULE}`,
                                    background: i < step ? RED : i === step ? RED : "#fff",
                                    color: i <= step ? "#fff" : "#9ca3af",
                                    opacity: i < step ? 0.45 : 1,
                                }}>
                                    {i < step ? "✓" : i + 1}
                                </div>
                                <span style={{
                                    fontSize: "0.7rem", fontWeight: 500,
                                    color: i === step ? DARK : "#9ca3af",
                                    whiteSpace: "nowrap",
                                }}>
                                    {st.label}
                                </span>
                            </div>
                            {i < STEPS.length - 1 && <div style={s.stepConnector} />}
                        </React.Fragment>
                    ))}
                </div>
                <p style={s.stepTitle}>{STEPS[step].title}</p>
                <p style={s.stepSubtitle}>{STEPS[step].subtitle}</p>
            </div>

            {/* Step bodies */}
            {step === 0 && (
                <div style={s.body}>
                    <span style={s.fieldLabel}>Fill in your details</span>
                    <FocusInput placeholder="First name *"    value={formData.firstName}      onChange={set('firstName')} />
                    <FocusInput placeholder="Last name *"     value={formData.lastName}       onChange={set('lastName')} />
                    <FocusInput type="email" placeholder="Email address *" value={formData.email} onChange={set('email')} style={{ marginBottom: 0 }} />
                </div>
            )}

            {step === 1 && (
                <div style={s.body}>
                    <span style={s.fieldLabel}>What best describes you?</span>
                    {ROLES.map((role) => {
                        const selected = formData.role === role.toLowerCase();
                        return (
                            <div key={role} style={s.radioItem(selected)} onClick={() => setRole(role.toLowerCase())}>
                                <div style={s.radioBox(selected)}>
                                    {selected && <div style={s.radioDot} />}
                                </div>
                                <span style={s.radioLabel}>{role}</span>
                            </div>
                        );
                    })}
                </div>
            )}

            {step === 2 && (
                <div style={s.body}>
                    <span style={s.fieldLabel}>How would you measure a successful experience?</span>
                    <FocusTextarea
                        rows={3}
                        placeholder="Tell us what success looks like for you..."
                        value={formData.measureSuccess}
                        onChange={set('measureSuccess')}
                    />
                    <span style={{ ...s.fieldLabel, marginTop: 18 }}>How did you hear about us?</span>
                    <FocusInput
                        placeholder="e.g. friend, social media, school..."
                        value={formData.hearAbout}
                        onChange={set('hearAbout')}
                        style={{ marginBottom: 0 }}
                    />
                </div>
            )}

            {/* Footer */}
            <div style={s.footer}>
                <button
                    style={{ ...s.btnBack, visibility: step > 0 ? "visible" : "hidden" }}
                    onClick={() => setStep(step - 1)}
                >
                    ← Back
                </button>
                <button
                    style={s.btnPrimary(canAdvance())}
                    disabled={!canAdvance()}
                    onClick={step < 2 ? () => setStep(step + 1) : handleSubmit}
                >
                    {step < 2 ? "Continue →" : "Submit"}
                </button>
            </div>

            <p style={s.note}>
                After submitting you'll receive a complete guide to Extern Academy's programs.
            </p>

        </div>
        </>
    );
}
